/*
  Cart scarcity notice: resolves the "slots left" number client-side.
*/
if (!customElements.get('cart-scarcity-count')) {
  class CartScarcityCount extends HTMLElement {
    connectedCallback() {
      const value = this.computeScarcityCount(this.dataset);

      if (value === null) return;

      this.textContent = String(value);
    }

    readScarcityCookie(key) {
      const match = document.cookie.match(new RegExp(`(?:^|; )${key}=([^;]*)`));
      return match ? match[1] : null;
    }

    readScarcitySeed() {
      if (CartScarcityCount.SEED !== null) return CartScarcityCount.SEED;

      let seed = parseInt(this.readScarcityCookie(CartScarcityCount.COOKIE_KEY), 10);

      if (!Number.isFinite(seed) || seed < 0) {
        seed = Math.floor(Math.random() * 1000);
        // No Expires/Max-Age on purpose: the seed is session-scoped, so every tab reads the
        // same number and it is cleared once the browser closes.
        document.cookie = `${encodeURIComponent(CartScarcityCount.COOKIE_KEY)}=${encodeURIComponent(seed)}; path=/; SameSite=Lax; Secure`;
      }

      CartScarcityCount.SEED = seed;

      return seed;
    }

    readScarcityCeiling(period) {
      try {
        const stored = window.theme?.storageUtil?.get(CartScarcityCount.CEILING_KEY, true, false);
        if (!stored || stored.p !== period || !Number.isFinite(stored.v)) return null;

        return stored.v;
      } catch {
        return null;
      }
    }

    writeScarcityCeiling(period, value) {
      try {
        window.theme?.storageUtil?.set(CartScarcityCount.CEILING_KEY, { p: period, v: value }, false);
      } catch {
        return;
      }
    }

    computeScarcityCount(dataset) {
      const min = parseInt(dataset.min, 10);
      const max = parseInt(dataset.max, 10);
      const year = parseInt(dataset.year, 10);
      const month = parseInt(dataset.month, 10);
      const day = parseInt(dataset.day, 10);

      if (!Number.isFinite(min) || !Number.isFinite(max) || max < min) return null;
      if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;

      // Day 0 of the next month resolves to the last day of this one.
      const daysInMonth = new Date(year, month, 0).getDate();
      const progress = daysInMonth > 1 ? (day - 1) / (daysInMonth - 1) : 0;
      const span = max - min;

      // Capped at half the span so the narrowed band below never inverts the curve.
      const jitterRange = Math.max(0, Math.min(Math.round(span * 0.1), Math.floor(span / 2)));

      // Base walks a band inset by jitterRange, so base + offset spans exactly [min, max].
      const base = max - jitterRange - (span - jitterRange * 2) * progress;

      // Jitter comes from the seed and is never re-rolled, so re-renders keep the same number.
      const offset = (this.readScarcitySeed() % (jitterRange * 2 + 1)) - jitterRange;
      let value = Math.min(max, Math.max(min, Math.round(base) + offset));

      const period = `${year}-${month}`;
      const stored = this.readScarcityCeiling(period);

      // A ceiling outside the live range is meaningless, so clamp it instead of discarding it.
      const ceiling = stored === null ? null : Math.min(max, Math.max(min, stored));

      if (ceiling !== null) value = Math.min(value, ceiling);
      value = Math.min(max, Math.max(min, value));

      if (stored === null || stored !== value) this.writeScarcityCeiling(period, value);

      return value;
    }
  }

  // Assigned rather than declared as static class fields, matching the theme's own scripts.
  CartScarcityCount.COOKIE_KEY = 'cart_scarcity_seed';
  CartScarcityCount.CEILING_KEY = 'cart-scarcity-ceiling';
  CartScarcityCount.SEED = null;

  customElements.define('cart-scarcity-count', CartScarcityCount);
}
